// Data hooks for the silo + counts + silo-calibration screens (plan.md G22).
// Thin wrappers over hooks/useApi.ts's useApiQuery/useApiMutation (the shared
// primitives every web module goal builds on — see that file's header), scoped to
// exactly the endpoints this goal's screens call:
//   - services/api/src/modules/silo/{readings,counts}.ts  (§8.2, §8.6)
//   - services/api/src/modules/config/locations.ts's nested silo-calibration
//     resource + read-only lookups (locations/products/sites) this module needs
//     but does not own.
import { useQueryClient } from '@tanstack/react-query';
import type {
  CompleteStockCountResponse,
  CreateSiloReadingRequest,
  CreateStockCountRequest,
  PopulateStockCountResponse,
  PostSiloReadingReconciliationResponse,
  ReverseSiloReadingResponse,
  SiloReadingResponse,
  StockCountLineResponse,
  StockCountResponse,
  StockCountWithLinesResponse,
  UpsertSiloCalibrationInput,
  UpsertStockCountLineRequest,
} from '@zebra/core';
import { ApiRequestError } from '../../api/client';
import { useApiMutation, useApiQuery } from '../../hooks/useApi';
import type { LocationDto, PaginatedResponse, ProductDto, SiloCalibrationDto, SiteDto } from './types';

// ---------------------------------------------------------------------------
// Read-only lookups this module consumes but does not own (config module, G13).
// ---------------------------------------------------------------------------

export function useSiloLocations() {
  return useApiQuery<PaginatedResponse<LocationDto>>(['config', 'locations', 'SILO'], '/config/locations', {
    query: { type: 'SILO', limit: 200 },
  });
}

/** Every location at a site, any type — stock-count lines (§8.6) can count
 * product at any location (yard, warehouse, bin, silo…), unlike silo readings
 * which are SILO-only. */
export function useLocations(siteId: string | undefined) {
  return useApiQuery<PaginatedResponse<LocationDto>>(['config', 'locations', 'bySite', siteId ?? 'all'], '/config/locations', {
    query: { siteId, limit: 200 },
    enabled: Boolean(siteId),
  });
}

export function useSites() {
  return useApiQuery<PaginatedResponse<SiteDto>>(['config', 'sites'], '/config/sites', {
    query: { limit: 200 },
  });
}

export function useActiveProducts() {
  return useApiQuery<PaginatedResponse<ProductDto>>(['config', 'products', 'active'], '/config/products', {
    query: { limit: 200, isActive: true },
  });
}

// ---------------------------------------------------------------------------
// Silo calibration — nested 1:1 resource on a SILO location (sole owner: G22).
// GET 404s when nothing is configured yet; callers treat that as "unconfigured"
// rather than a real error (see isCalibrationMissing below).
// ---------------------------------------------------------------------------

export function useSiloCalibration(locationId: string | null) {
  return useApiQuery<SiloCalibrationDto>(
    ['config', 'locations', locationId, 'silo-calibration'],
    `/config/locations/${locationId}/silo-calibration`,
    { enabled: Boolean(locationId), retry: false },
  );
}

/** True when a `useSiloCalibration` query's error is the expected "not configured
 * yet" 404, as opposed to a real failure (network, 500, etc.) that should still be
 * surfaced as an error. */
export function isCalibrationMissing(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'NOT_FOUND';
}

export function useUpsertSiloCalibration(locationId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<SiloCalibrationDto, UpsertSiloCalibrationInput>(
    `/config/locations/${locationId}/silo-calibration`,
    'PUT',
    {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['config', 'locations', locationId, 'silo-calibration'] });
      },
    },
  );
}

// ---------------------------------------------------------------------------
// Silo readings (§8.2) — services/api/src/modules/silo/readings.ts
// ---------------------------------------------------------------------------

export function useSiloReadings(params: { locationId?: string; limit?: number } = {}) {
  return useApiQuery<PaginatedResponse<SiloReadingResponse>>(
    ['silo', 'readings', params.locationId ?? 'all', params.limit ?? 20],
    '/silo/readings',
    { query: { locationId: params.locationId, limit: params.limit ?? 20 } },
  );
}

export function useSiloReading(readingId: string | undefined) {
  return useApiQuery<SiloReadingResponse>(['silo', 'readings', 'byId', readingId], `/silo/readings/${readingId}`, {
    enabled: Boolean(readingId),
  });
}

/** Most recent POSTED reading for one location — the silo status overview's
 * "latest reading / estimated level" per silo (BUILD_SPEC.md §8.2). POSTED only:
 * a REVERSED reading is a retracted observation and must not present as the
 * silo's current level (after a reverse, the prior POSTED reading — or the
 * empty state — shows instead). `useReverseSiloReading`'s ['silo','readings']
 * invalidation covers this key, so the card refreshes immediately after a
 * reverse. */
export function useLatestSiloReading(locationId: string) {
  return useApiQuery<PaginatedResponse<SiloReadingResponse>>(
    ['silo', 'readings', 'latest', locationId],
    '/silo/readings',
    { query: { locationId, limit: 1, status: 'POSTED' } },
  );
}

export function useCreateSiloReading() {
  const queryClient = useQueryClient();
  return useApiMutation<SiloReadingResponse, CreateSiloReadingRequest>('/silo/readings', 'POST', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['silo', 'readings'] });
    },
  });
}

export function usePostSiloReadingReconciliation(readingId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<PostSiloReadingReconciliationResponse, void>(`/silo/readings/${readingId}/post`, 'POST', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['silo', 'readings'] });
    },
  });
}

/** POSTED -> REVERSED (services/api modules/silo/readings.ts /reverse, added by
 * the 2026-07-07 spec-parity audit): backs out the reading's RECONCILIATION
 * adjustment with a reversing ledger entry, or — for a zero-variance reading
 * that posted no movement — just flips the status. Role gate matches the API's
 * requireRole('OPERATIONS', 'ADMIN') via useCanOperate at the call sites. */
export function useReverseSiloReading(readingId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<ReverseSiloReadingResponse, void>(`/silo/readings/${readingId}/reverse`, 'POST', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['silo', 'readings'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Stock counts (§8.6) — services/api/src/modules/silo/counts.ts
// ---------------------------------------------------------------------------

export function useStockCounts(params: { siteId?: string; status?: string } = {}) {
  return useApiQuery<PaginatedResponse<StockCountResponse>>(
    ['silo', 'counts', 'list', params.siteId ?? 'all', params.status ?? 'all'],
    '/silo/counts',
    { query: { siteId: params.siteId, status: params.status, limit: 100 } },
  );
}

export function useStockCount(countId: string | undefined) {
  return useApiQuery<StockCountWithLinesResponse>(['silo', 'counts', 'byId', countId], `/silo/counts/${countId}`, {
    enabled: Boolean(countId),
  });
}

export function useCreateStockCount() {
  const queryClient = useQueryClient();
  return useApiMutation<StockCountResponse, CreateStockCountRequest>('/silo/counts', 'POST', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['silo', 'counts', 'list'] });
    },
  });
}

export function useStartStockCount(countId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<StockCountResponse, void>(`/silo/counts/${countId}/start`, 'POST', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['silo', 'counts'] });
    },
  });
}

export function useCancelStockCount(countId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<StockCountResponse, void>(`/silo/counts/${countId}/cancel`, 'POST', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['silo', 'counts'] });
    },
  });
}

export function useUpsertStockCountLine(countId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<StockCountLineResponse, UpsertStockCountLineRequest>(`/silo/counts/${countId}/lines`, 'PUT', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['silo', 'counts', 'byId', countId] });
    },
  });
}

/** Bulk-populate (services/api modules/silo/counts.ts `/populate`, G-B): inserts one
 * count line per current stock_levels row at the count's site that isn't already on
 * the count. Invalidates the same `byId` key `useUpsertStockCountLine` does, so the
 * lines table + completeness indicator refresh immediately. */
export function usePopulateStockCount(countId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<PopulateStockCountResponse, void>(`/silo/counts/${countId}/populate`, 'POST', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['silo', 'counts', 'byId', countId] });
    },
  });
}

export function useCompleteStockCount(countId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<CompleteStockCountResponse, void>(`/silo/counts/${countId}/complete`, 'POST', {
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['silo', 'counts'] });
    },
  });
}
